import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, Button, Group, Modal, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconShare3 } from "@tabler/icons-react";

import { GuestInfoBite } from "./GuestInfoBite";
import InfoCard from "./info-card";
import InfoCardGrid from "./info-card-grid";

export function GuestInfo() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeInfo, setActiveInfo] = useState(-1);
  const [previousActiveInfo, setPreviousActiveInfo] = useState(-1);

  const informations = t("guest.info", {
    returnObjects: true,
  }) as GuestInfoBite[];

  const findIndexFromSlug = useCallback(
    (slug: string) => informations.findIndex((info) => info._slug === slug),
    [informations],
  );

  useEffect(() => {
    const hashSlug = location.hash.slice(1);
    if (hashSlug) {
      const index = findIndexFromSlug(hashSlug);
      if (index !== -1) setActiveInfo(index);
    }
  }, [location.hash, informations, findIndexFromSlug]);

  const handleOpen = (slug: string) => {
    const index = findIndexFromSlug(slug);
    setActiveInfo(index);
    navigate(`#${slug}`, { replace: true });
  };

  const handleClose = () => {
    setPreviousActiveInfo(activeInfo);
    setActiveInfo(-1);
    navigate(location.pathname, { replace: true });
  };

  const handleShare = async () => {
    const info = informations[activeInfo];
    const url = `${window.location.protocol}//${window.location.host}/guests#${info._slug}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Villa Braou Info - ${info.title}`,
          url,
        });
      } catch (error) {
        console.log(error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        notifications.show({
          color: "sea",
          message:
            "Sharing isn't supported here, but the link is copied to your clipboard.",
        });
      } catch {
        notifications.show({ color: "red", message: "Could not copy link." });
      }
    }
  };

  const active = activeInfo >= 0 ? informations[activeInfo] : null;

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={1}>{t("menu.stay.guests")}</Title>
        <Text c="dimmed" maw={680}>
          Local know-how, house rules and a few good links - tap any card for
          details.
        </Text>
      </Stack>

      <Box>
        <InfoCardGrid
          informations={informations}
          handleOpen={handleOpen}
          previousActiveInfo={previousActiveInfo}
        />
      </Box>

      <Modal
        opened={active !== null}
        onClose={handleClose}
        title={
          active && (
            <Text fz="xl" fw={600} ff='"Fraunces", serif'>
              {active.title}
            </Text>
          )
        }
        size="lg"
      >
        {active && <InfoCard information={active} />}
        <Group justify="flex-end" mt="lg">
          <Button
            variant="subtle"
            leftSection={<IconShare3 size={16} />}
            onClick={handleShare}
          >
            Send to a friend
          </Button>
          <Button onClick={handleClose} color="sea">
            Close
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}

export default GuestInfo;
